import { useState } from 'react';
import { Plane } from 'lucide-react';

interface VerifyEmailProps {
  email: string;
  password?: string;
  onConfirm: (email: string, code: string, password?: string) => Promise<void>;
  onResendCode: (email: string) => Promise<void>;
  onBack: () => void;
}

export function VerifyEmail({ email, password, onConfirm, onResendCode, onBack }: VerifyEmailProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await onConfirm(email, code, password);
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error('Verification failed');
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setResending(true);
    setResendSuccess(false);

    try {
      await onResendCode(email);
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (err: unknown) {
      const error = err instanceof Error ? err : new Error('Failed to resend code');
      setError(error.message);
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="w-full max-w-md p-8 space-y-6 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700 shadow-2xl">
        <div className="text-center space-y-2">
          <div className="flex justify-center">
            <div className="relative">
              <Plane className="w-12 h-12 text-blue-400 animate-bounce" />
              <div className="absolute inset-0 blur-xl bg-blue-400/20"></div>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white">Check your email</h1>
          <p className="text-slate-400">
            We've sent a verification code to
          </p>
          <p className="text-blue-400 font-medium">{email}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-slate-300 mb-2">
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              maxLength={6}
              placeholder="123456"
              className="w-full px-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-center text-2xl tracking-widest placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || code.length < 4}
            className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <Plane className="w-4 h-4 animate-spin" />
                <span>Verifying...</span>
              </div>
            ) : (
              'Verify Email'
            )}
          </button>
        </form>

        <div className="text-center">
          <button
            onClick={onBack}
            className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
          >
            Back to sign in
          </button>
        </div>

        <div className="text-center space-y-2">
          <div className="text-xs text-slate-500">
            Didn't receive the code? Check your spam folder
          </div>
          {resendSuccess && (
            <div className="text-green-400 text-sm">
              ✓ Verification code resent!
            </div>
          )}
          <button
            onClick={handleResendCode}
            disabled={resending}
            className="text-blue-400 hover:text-blue-300 text-sm transition-colors disabled:opacity-50"
          >
            {resending ? 'Sending...' : 'Resend verification code'}
          </button>
        </div>
      </div>
    </div>
  );
}
